import { useState, useEffect, useRef } from 'react';

export default function CustomCursor() {
  const posRef = useRef({ x: -100, y: -100 });
  const targetRef = useRef({ x: -100, y: -100 });
  const [glowPos, setGlowPos] = useState({ x: -100, y: -100 });
  const [hovering, setHovering] = useState(false);
  const [visible, setVisible] = useState(false);
  const rafRef = useRef<number>(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const handleMove = (e: MouseEvent) => {
      targetRef.current = { x: e.clientX, y: e.clientY };
      if (!visible) setVisible(true);
    };

    const handleOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('a') ||
        target.closest('button') ||
        target.closest('[data-cursor]')
      ) {
        setHovering(true);
      }
    };

    const handleOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('a') ||
        target.closest('button') ||
        target.closest('[data-cursor]')
      ) {
        setHovering(false);
      }
    };

    const handleLeave = () => setVisible(false);
    const handleEnter = () => setVisible(true);

    // Smooth interpolation loop
    const animate = () => {
      if (!mountedRef.current) return;
      const dx = targetRef.current.x - posRef.current.x;
      const dy = targetRef.current.y - posRef.current.y;
      posRef.current = {
        x: posRef.current.x + dx * 0.12,
        y: posRef.current.y + dy * 0.12,
      };
      setGlowPos({ ...posRef.current });
      rafRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMove, { passive: true });
    document.addEventListener('mouseover', handleOver);
    document.addEventListener('mouseout', handleOut);
    document.addEventListener('mouseleave', handleLeave);
    document.addEventListener('mouseenter', handleEnter);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseover', handleOver);
      document.removeEventListener('mouseout', handleOut);
      document.removeEventListener('mouseleave', handleLeave);
      document.removeEventListener('mouseenter', handleEnter);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (typeof window === 'undefined') return null;

  return (
    <div
      className="fixed pointer-events-none z-[9998] hidden md:block"
      style={{
        left: glowPos.x,
        top: glowPos.y,
        transform: 'translate(-50%, -50%)',
        width: hovering ? 320 : 200,
        height: hovering ? 320 : 200,
        borderRadius: '50%',
        background: hovering
          ? 'radial-gradient(circle, oklch(var(--accent-500) / 0.12) 0%, oklch(var(--accent-500) / 0.04) 40%, transparent 70%)'
          : 'radial-gradient(circle, oklch(var(--accent-500) / 0.06) 0%, oklch(var(--accent-500) / 0.02) 40%, transparent 70%)',
        opacity: visible ? 1 : 0,
        transition: 'width 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94), height 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94), background 400ms ease',
      }}
    />
  );
}